/**
 * CAOS-570 — what a component says about itself, beyond what it is called.
 *
 * A piece in the library could already say what it is, what element it registers and what group it
 * belongs to. It could not say what a page may set on it, what it raises back, what goes inside it,
 * which design tokens move it, or what it looks like doing its job in each of its variants. All of
 * that lived either in somebody's head or in a hand-kept map in the workspace keyed by element name
 * — which meant the workspace knew about individual components by name, and a component arriving in
 * a package could never be added to it, and therefore could never be documented.
 *
 * These are the shapes a component declares instead. A component ships its documentation WITH
 * ITSELF, in `<component>.docs.ts` beside its source, the way it already ships its element and its
 * purpose. Everything the reference page shows, it shows because a component declared it.
 *
 * ── UNSAID IS NOT EMPTY, at every level ──────────────────────────────────────
 * Every section here is optional, and absence is a THIRD state rather than a synonym for none:
 *
 *   absent  ⇒ this piece has not said. The page reads "not documented yet".
 *   `[]`    ⇒ this piece has said there are none. The page reads "raises no events".
 *   filled  ⇒ the contents.
 *
 * The two are never collapsed. A page that turns silence into "there are none" states a confident
 * claim nobody made, and a component reference is precisely where somebody would believe it.
 *
 * ── THE VOCABULARY IS THE MANIFEST'S ─────────────────────────────────────────
 * `placements` and the input type names are the Component MetaType's own words (CAOS-387), not a
 * second set invented here, so the day a component's documentation travels in its manifest the two
 * halves do not have to be reconciled. Where this file names something the manifest does not carry
 * yet — examples, events, slots, styling hooks, methods — it is the shape the manifest should grow.
 */

/**
 * The kinds of place a component declares it may be dropped into — the Component MetaType's enum.
 *
 * PLACEMENT IS OPT-IN, here as there: absent means the piece has not said, an empty list means it
 * says nowhere, and neither is ever read as "anywhere". `seam` is a shell injection point; the last
 * three name surfaces the platform does not have yet and are declarable so the vocabulary does not
 * have to widen later.
 */
export type ComponentPlacement =
  | 'recordPage'
  | 'homePage'
  | 'appPage'
  | 'recordAction'
  | 'utilityBar'
  | 'screen'
  | 'seam';

/** How a placement reads in words, so the page never prints a vocabulary word at a person. */
export const PLACEMENT_LABELS: Readonly<Record<ComponentPlacement, string>> = {
  recordPage: 'a record page',
  homePage: "an app's home page",
  appPage: 'a page inside an app',
  recordAction: 'an action on a record',
  utilityBar: 'the utility bar',
  screen: 'a step of a guided flow',
  seam: 'a seam in the workspace frame',
};

/**
 * The design-time type of an input. A CLOSED set, deliberately small, and it is the closed-ness
 * that matters rather than the members: the incumbent's catalogue carries 40 distinct type strings
 * across 736 attributes — `string` and `String` as separate spellings, `list`, `array` and `Array`
 * all present, one-offs like `Array<{ label: string; value: string; }>`, and 34 attributes with no
 * type at all. Nothing can validate that. This set maps onto the storage kinds the platform already
 * declares, and an input whose type is not in it does not compile.
 */
export type ComponentInputType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'recordId'
  | 'objectRef'
  | 'fieldRef'
  | 'json';

/**
 * Where an input's value comes from — the Component MetaType's own `ComponentInputSourceSchema`
 * (`kernel/src/meta/component.ts`, CAOS-562), duplicated here rather than imported for the same
 * reason {@link ComponentInputType} and {@link ComponentPlacement} already are: this file ships
 * with `@caos/ui`, which does not depend on the kernel package, and the manifest generator is what
 * carries these words from one vocabulary to the other. Keep the members identical to the kernel's.
 *
 *   literal            — an administrator types it when placing the piece
 *   recordField        — a field of the record the surface is bound to
 *   urlParameter       — a named parameter of the route that opened the surface
 *   orgContext         — a fact about the organisation
 *   userContext        — a fact about the person reading it
 *   automationVariable — a variable supplied by the automation that invoked the surface
 *
 * Absent ⇒ `literal`. Most inputs across this library are configuration an author chooses when
 * composing a page — a variant, a size, a heading fallback — and leaving `source` unset for those
 * is the honest, schema-documented default rather than a gap. Setting it explicitly is reserved for
 * an input this component's own documentation shows is genuinely bound to something other than a
 * literal (CAOS-562) — never inferred from the attribute's name alone.
 */
export type ComponentInputSource = 'literal' | 'recordField' | 'urlParameter' | 'orgContext' | 'userContext' | 'automationVariable';

/**
 * One value a page may set on a piece.
 *
 * BOTH NAMES are carried, deliberately. `name` is the property an author writes in code; `attribute`
 * is the hyphenated form written in markup. The incumbent publishes both for every input because the
 * two are used in different places, and making somebody transliterate is a tax paid on every line.
 * An input with no `attribute` is one that can only be set as a property — a whole record set, a
 * list of options, an injected search — and saying so is useful rather than an omission.
 */
export interface ComponentInputDoc {
  /** The property name. CONTRACT: a placed instance stores its value under this key. */
  readonly name: string;
  /** The hyphenated attribute, when the piece observes one. Absent ⇒ settable only as a property. */
  readonly attribute?: string;
  readonly type: ComponentInputType;
  /**
   * The values this input accepts, when it accepts a closed set of them.
   *
   * A type alone says `text` for an input that will only ever take one of four words, which is true
   * and useless — a person placing it still has to go and read the source to find the four. It also
   * matters for the opposite reason: the incumbent's largest entry selects between SIXTEEN
   * behaviours from an undeclared type string, and naming the members is the difference between a
   * closed set somebody can check and a magic word they have to know.
   */
  readonly options?: readonly string[];
  /**
   * What every OTHER value means, when the values above are recognised but nothing else is refused.
   *
   * CAOS-653. Some inputs do not take a closed set at all: the overflow menu's `align` tests for
   * `"start"` and treats everything else, absence included, as right alignment. Declaring
   * `options: ['start']` would be the wrong statement twice over — it reads as a constraint the
   * component does not enforce, and it would become one, because the platform refuses a placement
   * whose value is outside a declared set. Naming the fallback says the true thing instead: these
   * are recognised, anything else behaves this way, and nothing is refused.
   *
   * Present ⇒ the set is OPEN, and the manifest carries no closed set for this input.
   */
  readonly fallback?: string;
  /** One line telling the person placing it what this input is for. */
  readonly description: string;
  /**
   * What a placement gets when the author sets nothing. Absent ⇒ the piece declares no default.
   *
   * A LIST is allowed, and it is a different statement from absence. Several pieces start from an
   * empty list — a table with no rows, a caret with no recents, a bar with no persona options — and
   * "starts empty" is a fact a placement can rely on, where "has not said" is not. Before CAOS-653
   * this could only be written as a sentence, so the one thing a reader most wanted to know about a
   * list input was the one thing its declaration could not carry.
   *
   * Only an input whose type is `json` may default to a list; a text or number input defaulting to
   * a list would be declaring a value its own type cannot hold.
   */
  readonly default?: string | number | boolean | null | readonly (string | number | boolean)[];
  /** Absent ⇒ optional. Only the handful that genuinely cannot be defaulted should carry it. */
  readonly required?: boolean;
  /** Where the value comes from. See {@link ComponentInputSource}. Absent ⇒ `literal`. */
  readonly source?: ComponentInputSource;
  /**
   * There is a JS PROPERTY of {@link name} behind this input, not only the attribute.
   *
   * CAOS-653, and it is declared this way round because of what the library actually contains: of
   * 237 inputs that carry an attribute, 23 have a property accessor. The rest are set as markup and
   * read back off the element — some through `getAttribute`, some never read in script at all
   * because they exist only as a styling hook (`:host([variant="primary"])`). Nothing in this
   * package reflects an attribute into a property; `base-element.ts` defines no
   * `attributeChangedCallback`.
   *
   * So `name` is the key a placement stores under, and it is NOT a promise that a property of that
   * name exists. Marking the 23 that do is the honest, small statement. Marking the 168 that do not
   * would put the notice on the ordinary case and leave the exception silent.
   *
   * Absent, with an `attribute` ⇒ set as markup. Absent, with no `attribute` ⇒ property only.
   */
  readonly property?: true;
  /**
   * What reading this input back gives, when that is not what writing it sets.
   *
   * CAOS-653. A field's `value` is the case: the setter writes the attribute, and the getter
   * returns the live contents of the inner control. They agree until somebody types, and then they
   * do not — the attribute holds what was last written and the property holds what is on screen.
   * Both behaviours are wanted, and an input declared as one value with one meaning cannot say so.
   */
  readonly readsBack?: string;
}

/** Something the piece raises back, so a page can act on it. */
export interface ComponentEventDoc {
  readonly name: string;
  /** When it fires, in the words a person would use. */
  readonly when: string;
  /** What its detail carries. `nothing` is a real answer and reads better than an empty cell. */
  readonly carries: string;
}

/** A place content goes inside the piece. The default slot is named with an empty string. */
export interface ComponentSlotDoc {
  readonly name: string;
  readonly takes: string;
}

/**
 * A named piece of the component's own inside that a stylesheet may reach.
 *
 * Tokens are half of restyling and parts are the other half: a token changes a value the component
 * already reads, a part lets somebody style a piece of it the component never thought to expose a
 * value for. Every component in this library already names its parts in its own header, and a
 * reference that showed only the tokens would leave a subscriber restyling the platform reading
 * source for the rest of it.
 */
export interface ComponentPartDoc {
  /** The part name, as it is written in `::part(…)`. */
  readonly part: string;
  /** Which piece of the component it is. */
  readonly is: string;
  /**
   * WHEN the part is there, for one that is not always there.
   *
   * CAOS-653. A detail section carries `toggle` only while it is collapsible — the header becomes a
   * button and takes the second part name at that moment, and a section that cannot fold exposes
   * nothing under that name. A field renderer's parts depend on the mode it is drawing in. Declared
   * flat, every part reads as permanently available, so a stylesheet written against one would
   * simply never match and the reason would be nowhere in the reference.
   *
   * Absent ⇒ always exposed, which is the ordinary case.
   */
  readonly when?: string;
}

/**
 * A design token the piece honours, and what it moves.
 *
 * This is the table a subscriber restyling the platform reads, and guessing is how a brand ends up
 * half-applied. It is also where the design system stays honest: a token that NAMES A COMPONENT is
 * a missing axis rather than a component's private setting, and this table is where one becomes
 * visible instead of staying buried in a stylesheet.
 */
export interface ComponentTokenDoc {
  readonly token: string;
  readonly controls: string;
}

/** One argument of a method. */
export interface ComponentMethodArgDoc {
  readonly name: string;
  readonly type: string;
  readonly description: string;
}

/**
 * Something a page may READ BACK off the piece, but cannot set.
 *
 * This section exists because documenting the library found that it was missing. A menu exposes
 * whether its panel is open; a field exposes what is currently in it. Neither is an INPUT — nothing
 * can set it — and neither is a METHOD, because there is no call to make. Without a section of its
 * own a reader scanning the reference simply never learns it is there, which matters most for
 * exactly the audience this page is for: somebody composing our piece inside their own component and
 * needing to know what they can ask it.
 */
export interface ComponentStateDoc {
  /** The property name a page reads. */
  readonly name: string;
  /** What kind of value comes back, in plain words. */
  readonly type: string;
  readonly description: string;
}

/**
 * Something else drives this piece by calling it — the controlled popovers, mostly. A component a
 * page can place but never call is only half placed.
 */
export interface ComponentMethodDoc {
  readonly name: string;
  readonly description: string;
  /** Absent ⇒ not stated. `[]` ⇒ it takes none. */
  readonly args?: readonly ComponentMethodArgDoc[];
}

/**
 * A PLACEMENT — a piece with values set on it, holding other pieces. Data, never a function.
 *
 * The same tree is walked twice: once to build the elements a reader looks at, and once to print
 * the code underneath them. That is the whole reason an example is data. The incumbent's code
 * sample is authored beside the component and goes stale on its own schedule, because nothing
 * connects the sample to the rendering; ours cannot drift, because the code IS what made the
 * preview.
 *
 * `component` is the element tag — the published half of the contract, the same string a page
 * stores on a placed instance and the same string somebody writing their own component types into
 * their template.
 *
 * `children` is the honest part. The page manifest's component instance carries `key` and a value
 * map and has NO field for what goes INSIDE a placed piece, so an example whose piece takes slotted
 * content (a button's label, a section's rows, a bar's actions) cannot be expressed as a stored
 * placement today. It is declared here because the documentation has to be true, and the gap it
 * exposes is filed rather than papered over — which is exactly what documenting the library is for.
 */
export interface PlacementNode {
  /** The element tag being placed. */
  readonly component: string;
  /** The values set on it. Primitives are written as attributes; anything else as a property. */
  readonly inputs?: Readonly<Record<string, unknown>>;
  /**
   * Which named slot of its parent this goes into.
   *
   * Declared here rather than as an input, because it is not one: `slot` belongs to the parent's
   * contract, not to this piece's, and listing it among the values set on the piece would print a
   * placement claiming the piece takes an input it does not have.
   */
  readonly slot?: string;
  /** What goes inside it — text, or further placements. */
  readonly children?: readonly (string | PlacementNode)[];
}

/**
 * What an example needs from its stage BEYOND the space the piece takes standing still (CAOS-587).
 *
 * Most interesting pieces are bigger doing their job than they are at rest: a menu, a tooltip, a
 * picker, a lookup showing its results. A page whose whole purpose is showing a piece WORKING has
 * to make room for that, and sizing every stage for the largest possible case would be wrong for
 * the forty that open nothing. So the room is declared by the example that knows what it opens.
 *
 * Declaring room also takes the example out of the sideways scroller a stage otherwise puts it in,
 * which is deliberate: a scroller clips on BOTH axes, which is the thing that was being fixed.
 *
 * An example that is ALSO too wide for the stage says so with `needsWidth`, and then gets both: it
 * is put back in the scroller, and the scroller is told to keep the same room inside itself. Being
 * wide and opening something were mutually exclusive until that existed, so the first example that
 * needed both would have quietly lost one of them (CAOS-655).
 */
export interface ExampleStage {
  /** Room kept clear BELOW the piece for what it opens downward, in pixels. */
  readonly opensBelow?: number;
  /** Room kept clear ABOVE it — a tooltip sits on top of the thing it describes. */
  readonly opensAbove?: number;
  /**
   * Sideways room this example needs, in pixels — it is wider than the stage (CAOS-655).
   *
   * The piece scrolls sideways inside its own box rather than widening the page, which is what a
   * stage does by default for anything that opens nothing. Saying it explicitly is what makes it
   * compatible with `opensBelow` / `opensAbove`: the room those declare is kept INSIDE the scroller
   * as well, so the box clips neither axis and the example can be wide and open something at once.
   */
  readonly needsWidth?: number;
  /** Hold a piece that covers the whole screen inside a box this tall, so it can be looked at. */
  readonly contain?: number;
  /** Put the piece at the RIGHT-HAND END, for a piece whose panel aligns to its own right edge. */
  readonly alignEnd?: boolean;
  /**
   * How several pieces in one example stand next to each other. `row` unless said otherwise.
   *
   * `column` is not cosmetic: an example demonstrating a placeholder that keeps a REGION's shape, or
   * a run of label-and-value rows, says something different stacked than it does in a line, and a
   * row is the wrong answer for both.
   */
  readonly arrange?: 'row' | 'column';
}

/**
 * What the PAGE AROUND an example does, when the example needs one.
 *
 * A few pieces are only half of something: a lookup needs somewhere to search, the top bar reports
 * a theme press and leaves the acting to its host, a strip of open records reports a close and the
 * shell drops the record. Left unhosted those examples draw a control that does nothing, which is a
 * working example teaching that the piece does nothing.
 *
 * A placement has no way to say any of this — it carries stored values, not behaviour — so this is
 * a declared gap rather than a quiet workaround. Two things keep it honest: it is declared BY THE
 * COMPONENT, beside itself, so the workspace still holds no per-component knowledge; and the page
 * SHOWS the reader that this example includes what the surrounding page does, rather than passing
 * host behaviour off as something the piece does on its own.
 */
export interface ExampleHost {
  /** One line, shown on the page, saying what the surrounding page is doing here. */
  readonly summary: string;
  /** Wire it up. Runs once, on the built example, after it is on the stage. */
  readonly run: (stage: HTMLElement, page: PageSeams) => void;
}

/**
 * The seams an example may reach that belong to the APPLICATION, not to the component.
 *
 * Handed in rather than imported, and the distinction is load-bearing. A component library sits
 * BELOW the workspace that draws it and cannot import from it, so an example that needs one of the
 * workspace's own settings has exactly two options: be given it, or write a second implementation of
 * it. The second is the trap — a private copy of the light/dark preference read the attribute
 * stamped on the page instead of the stored setting, so pressing the example turned the page dark
 * while the real control above it still said System, pressing the real one left the example claiming
 * Dark on a light page, and neither choice survived a reload. Two controls for one setting that
 * disagree with each other is a worse lesson than no example at all.
 *
 * So the workspace passes its own seam down, and there is one implementation of it.
 */
export interface PageSeams {
  /** The page's light/dark setting: read it, cycle it, and hear about it when anything else changes it. */
  readonly theme: {
    readonly current: () => string;
    /** Advance to the next setting and apply it for real. Returns what it became. */
    readonly cycle: () => string;
    /** Called whenever the setting changes, from here or from anywhere else. Returns an unsubscribe. */
    readonly onChange: (fn: (pref: string) => void) => () => void;
  };
}

/** One named example: what it demonstrates, and the placement that produces it. */
export interface ComponentExample {
  /** Stable key — it addresses the example in the picker and in a link to it. */
  readonly key: string;
  /** What the picker calls it. */
  readonly label: string;
  /** One sentence saying what this example demonstrates. */
  readonly summary: string;
  /** The placement that draws it. A list is drawn as a row of siblings. */
  readonly placement: PlacementNode | readonly PlacementNode[];
  readonly stage?: ExampleStage;
  readonly host?: ExampleHost;
}

/** A sibling piece this one gets confused with, and the distinction. */
export interface ComponentAlternative {
  /** The other piece's catalogue name, so the page can link to it. */
  readonly name: string;
  /** When you want that one instead of this one. */
  readonly when: string;
}

/**
 * Everything a component says about itself beyond its catalogue line.
 *
 * Every section is optional and absence means NOT SAID (see the header). A component that declares
 * this file at all is saying something; a component with no file at all has said nothing, and the
 * page tells those two apart.
 */
export interface ComponentDocs {
  /**
   * OTHER NAMES for this piece — what somebody arriving would call it before they knew ours.
   *
   * The failure this exists to prevent is specific and it is the expensive one: somebody builds a
   * component that already exists, or five people build the same thing five ways, because a search
   * for the word THEY use returns nothing and an empty result reads as "we do not have one". A
   * piece called StatusPill is not found by typing "badge", and "badge" is what most people type.
   */
  readonly aliases?: readonly string[];
  /**
   * WHAT SOMEBODY IS TRYING TO DO when this is the answer — findability by intent rather than by
   * name. Short phrases in a person's own words: "show that a save is in progress", "let somebody
   * pick one of a few options". A reference that can only be searched by the name of the thing you
   * do not know the name of is a reference that answers questions you did not need to ask.
   */
  readonly answers?: readonly string[];
  /** Where it may be placed. A GRANT: `[]` means nowhere, absent means it has not said. */
  readonly placements?: readonly ComponentPlacement[];
  readonly inputs?: readonly ComponentInputDoc[];
  readonly events?: readonly ComponentEventDoc[];
  /** What a page may read back off it but cannot set. See {@link ComponentStateDoc}. */
  readonly state?: readonly ComponentStateDoc[];
  readonly slots?: readonly ComponentSlotDoc[];
  readonly tokens?: readonly ComponentTokenDoc[];
  /** The named insides a stylesheet may reach. See {@link ComponentPartDoc}. */
  readonly parts?: readonly ComponentPartDoc[];
  readonly methods?: readonly ComponentMethodDoc[];
  /** How it is placed, in the context of a page and a region — prose, one short paragraph. */
  readonly placing?: string;
  /** When to reach for this one. */
  readonly whenToUse?: string;
  /** And what to use instead — the most useful paragraph in any component reference. */
  readonly insteadUse?: readonly ComponentAlternative[];
  /** What it manages on the author's behalf, and what the author is still responsible for. */
  readonly accessibility?: string;
  /**
   * WHERE THIS PIECE'S MATERIAL CAME FROM, and on what terms (CAOS-602).
   *
   * Almost every piece draws itself and has nothing to say here, which is why this is optional. It
   * exists for the ones that do not: artwork, path data, a wordlist, anything adopted from outside
   * that arrives under a licence. A licence that lives only in a source comment is a licence the
   * people reading the reference cannot see, and an obligation nobody can discharge without
   * opening the file — so when a piece has one, it says so on its own page, in prose, beside
   * everything else it declares about itself.
   */
  readonly provenance?: string;
  readonly examples?: readonly ComponentExample[];
}

/** The examples of one piece as a list, whichever shape its placement was declared in. */
export function exampleNodes(example: ComponentExample): readonly PlacementNode[] {
  return Array.isArray(example.placement)
    ? (example.placement as readonly PlacementNode[])
    : [example.placement as PlacementNode];
}
